import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    // Always restore body styles when the native <dialog> emits 'close',
    // regardless of how it was closed (JS, backdrop click, ESC, form submit).
    // Without this, body stays stuck on position:fixed and the header is unclickable.
    this.refs.dialog?.addEventListener('close', this.#restoreBodyStyles);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.refs.dialog?.removeEventListener('close', this.#restoreBodyStyles);
  }

  #restoreBodyStyles = () => {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    if (this.#previousScrollY) {
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
      this.#previousScrollY = 0;
    }
  };

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    // CRITICAL fix: restore body styles UPFRONT, before any early return or
    // await. Two known failure modes were leaving body stuck on position:fixed:
    //   1) early-return below when dialog.open was already false (closed by
    //      backdrop/ESC/form-submit)
    //   2) onAnimationEnd promise never resolving because the closing animation
    //      didn't register (Safari mobile, reduce-motion, layout shifts)
    // Either path resulted in the header being unclickable until hard reload.
    this.#restoreBodyStyles();

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    // Race the closing animation against a 1s timeout. Guarantees dialog.close()
    // is reached even when the animation never completes (mobile Safari edge).
    await Promise.race([
      onAnimationEnd(dialog, undefined, { subtree: false }),
      new Promise((resolve) => setTimeout(resolve, 1000)),
    ]);

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

/**
 * Body unlock auto-recovery (defensive last-resort).
 *
 * Some close paths fail to clear body.style.position='fixed' or
 * <html scroll-lock>, leaving the header unclickable. Causes observed:
 *   - Aborted closeDialog() animations (Safari mobile)
 *   - Third-party app dialogs (wishlist, reviews) closing without cleanup
 *   - Race between cart auto-open and quick subsequent navigation
 *
 * This observer watches body's style attribute. Whenever it changes, after
 * a short debounce it verifies: if body is locked but NO <dialog> is actually
 * open AND no element has the scroll-lock attribute, force-unlock everything.
 *
 * Idempotent and safe — only fires when state is provably inconsistent.
 */
(() => {
  if (typeof MutationObserver === 'undefined') return;

  let pending = null;

  const scheduleRecoveryCheck = () => {
    if (pending) return;
    pending = setTimeout(() => {
      pending = null;
      const bodyLocked = document.body.style.position === 'fixed';
      const htmlLocked = document.documentElement.hasAttribute('scroll-lock');
      if (!bodyLocked && !htmlLocked) return;

      const openDialog = document.querySelector('dialog[open]');
      const openDetailsLocked = document.querySelector('details[open][scroll-lock]');
      if (openDialog || openDetailsLocked) return;

      // Inconsistent state — force unlock.
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.documentElement.removeAttribute('scroll-lock');
    }, 350);
  };

  // Observe body's style attribute mutations (position:fixed gets set/removed)
  new MutationObserver(scheduleRecoveryCheck).observe(document.body, {
    attributes: true,
    attributeFilter: ['style'],
  });

  // Also observe html for scroll-lock attribute
  new MutationObserver(scheduleRecoveryCheck).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['scroll-lock'],
  });

  // Plus, anytime any dialog closes (native event), recheck.
  document.addEventListener('close', scheduleRecoveryCheck, true);
})();

/**
 * Layer 4 — zombie dialog detector (polling).
 *
 * Catches the case where closeDialog() started (animation class .dialog-closing
 * was added) but `dialog.close()` never ran — leaving the <dialog> logically
 * open. While the dialog is logically open, HTMLDialogElement's modal mode
 * BLOCKS ALL CLICKS outside it. This is why the header stops responding
 * even after the drawer visually fades out.
 *
 * Mutation observation cannot see this because no attribute changes after
 * the animation glitches. A 1s polling loop is the only reliable detector.
 *
 * Trigger: <dialog open> with .dialog-closing class for more than 1.5s.
 * Action: force dialog.close() + body cleanup.
 */
(() => {
  const POLL_MS = 1000;
  const ZOMBIE_THRESHOLD_MS = 1500;

  setInterval(() => {
    // Skip while the tab is backgrounded — a stuck dialog only matters when visible,
    // and the next poll after the tab returns catches it (saves battery on mobile).
    if (document.hidden) return;
    const zombies = document.querySelectorAll('dialog[open].dialog-closing');
    if (zombies.length === 0) return;

    const now = Date.now();
    zombies.forEach((d) => {
      const since = d.dataset.zombieFirstSeen ? parseInt(d.dataset.zombieFirstSeen, 10) : null;
      if (!since) {
        d.dataset.zombieFirstSeen = String(now);
        return;
      }
      if (now - since < ZOMBIE_THRESHOLD_MS) return;

      // Zombie confirmed — force close
      try { d.close(); } catch (_) {}
      d.classList.remove('dialog-closing');
      delete d.dataset.zombieFirstSeen;
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.documentElement.removeAttribute('scroll-lock');
    });

    // Clean up the dataset on dialogs that recovered
    document.querySelectorAll('dialog[data-zombie-first-seen]').forEach((d) => {
      if (!d.hasAttribute('open') || !d.classList.contains('dialog-closing')) {
        delete d.dataset.zombieFirstSeen;
      }
    });
  }, POLL_MS);
})();

/**
 * Layer 5 — capture pointerdown anywhere; if a <dialog open> exists AND the
 * pointer hit a target outside of it, the user is trying to interact with
 * content that the modal is blocking. Force close + body cleanup.
 *
 * Uses { capture: true } so it runs BEFORE the dialog's modal-blocking
 * intercepts. This catches the case where ALL OTHER paths to close the
 * dialog failed (third-party app, animation glitch, native dialog stuck).
 *
 * Only fires when the click target is NOT inside any dialog — avoids
 * misfiring on the drawer's own buttons.
 */
document.addEventListener(
  'pointerdown',
  (event) => {
    const openDialogs = document.querySelectorAll('dialog[open]');
    if (openDialogs.length === 0) return;
    const target = /** @type {Element} */ (event.target);
    if (!target || target.closest('dialog[open]')) return;

    // User wants to interact outside any open dialog — close them all
    openDialogs.forEach((d) => {
      try { d.close(); } catch (_) {}
    });
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    document.documentElement.removeAttribute('scroll-lock');
  },
  { capture: true, passive: true }
);
